(async () => {
    const { Core } = await importModule('Core');
    const { $hover } = await importModule('Utils');

    customElements.define('main-navigation', class extends Core {
        elements = {
            dropdowns: '*[data-drop]'
        }
        render() {
            this._masonryController?.abort();
            this._masonryController = new AbortController();
            this.$dropdowns.map(this._handleDropdown.bind(this));
            this.querySelectorAll('[data-masonry-menu]').forEach(menu => this._handleMasonry(menu));
        }

        disconnect() {
            this._masonryController?.abort();
        }
        _handleDropdown(drop) {
            $hover(drop, (e, state) => this.publish('lights-out', state));
        }

        _handleMasonry(menu) {
            const { signal } = this._masonryController;
            const triggers = [...menu.querySelectorAll('[data-jb-menu-trigger]')];
            const panels = [...menu.querySelectorAll('[data-jb-menu-panel]')];
            const images = menu.querySelector('[data-masonry-images]');
            const templates = new Map([...menu.querySelectorAll('[data-masonry-template]')]
                .map(template => [template.dataset.masonryTemplate, template]));
            const layers = new Map([...menu.querySelectorAll('[data-masonry-layer]')]
                .map(layer => [layer.dataset.masonryLayer, layer]));
            const ready = new Map();
            const decodeImages = element => Promise.all([...element.querySelectorAll('img')].map(async image => {
                image.loading = 'eager';
                try {
                    await image.decode();
                    return image.naturalWidth > 0;
                } catch {
                    return false;
                }
            })).then(results => results.every(Boolean));
            const background = images?.querySelector('[data-masonry-background]');
            const backgroundReady = background ? decodeImages(background) : Promise.resolve(false);
            let selection = 0;
            let currentLayer = [...layers.values()].find(layer => layer.classList.contains('is-active'));
            let pendingImage;
            let fading = false;
            let animation;

            const fade = async (layer, from, to, duration) => {
                if (!layer?.animate) return;
                animation = layer.animate([{ opacity: from }, { opacity: to }], {
                    duration,
                    easing: 'ease-in-out',
                    fill: 'forwards'
                });
                try {
                    await animation.finished;
                } catch {}
            };

            const showImage = async (layer, canAnimate) => {
                pendingImage = { layer, canAnimate };
                if (signal.aborted || fading || layer === currentLayer) return;
                fading = true;
                pendingImage = null;
                const outgoing = currentLayer;
                const animate = canAnimate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

                try {
                    if (animate) await fade(outgoing, 1, 0, 60);
                    if (signal.aborted) return;
                    if (pendingImage) {
                        layer = pendingImage.layer;
                        pendingImage = null;
                    }
                    outgoing?.classList.remove('is-active');
                    outgoing?.setAttribute('aria-hidden', 'true');
                    if (outgoing) outgoing.inert = true;
                    animation?.cancel();
                    layer.classList.add('is-active');
                    layer.setAttribute('aria-hidden', 'false');
                    layer.inert = false;
                    currentLayer = layer;
                    if (animate) await fade(layer, 0, 1, 100);
                } finally {
                    animation?.cancel();
                    animation = null;
                    fading = false;
                    if (!signal.aborted && pendingImage) showImage(pendingImage.layer, pendingImage.canAnimate);
                }
            };

            signal.addEventListener('abort', () => animation?.cancel(), { once: true });

            const activate = async trigger => {
                const index = trigger.dataset.jbMenuTrigger;
                const request = ++selection;
                pendingImage = null;
                triggers.forEach(item => item.classList.toggle('@active', item === trigger));
                panels.forEach(panel => panel.classList.toggle('@active', panel.dataset.jbMenuPanel === index));

                const template = templates.get(index);
                if (!images || !template) return;
                const key = template.dataset.masonryKey;
                let layer = layers.get(key);

                if (!layer) {
                    layer = template.content.firstElementChild.cloneNode(true);
                    layers.set(key, layer);
                    images.append(layer);
                    ready.set(key, decodeImages(layer));
                }

                if (ready.has(key) && !await ready.get(key)) return;
                const canAnimate = await backgroundReady;
                if (signal.aborted || request !== selection) return;

                showImage(layer, canAnimate);
            };

            triggers.forEach(trigger => {
                trigger.addEventListener('mouseenter', () => activate(trigger), { signal });
                trigger.addEventListener('focus', () => activate(trigger), { signal });
            });
        }
    });
})();
